import { Radio as BaseRadio } from '@base-ui/react/radio';
import { RadioGroup as BaseRadioGroup } from '@base-ui/react/radio-group';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn.ts';

export const radioOptionVariants = cva([
  /*
   * The border is always drawn and only changes colour, so picking an option
   * never moves the list under the pointer.
   */
  'flex w-full items-start gap-3 rounded-md border border-line bg-transparent px-3.5 py-3 text-left transition',
  'hover:border-ink-muted',
  'data-checked:border-ink data-checked:bg-fill-faint',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
  'data-disabled:pointer-events-none data-disabled:opacity-40',
]);

export type RadioOptionVariants = VariantProps<typeof radioOptionVariants>;

export interface RadioOptionProps<Value = string> extends Omit<
  BaseRadio.Root.Props<Value>,
  'style' | 'className' | 'children'
> {
  /** What the option says. The row is the control, so this is not a label element. */
  label: string;
  /** One line under the label saying what picking it means. */
  description?: string;
  className?: string;
}

export interface RadioOptionGroupProps<Value = string> extends Omit<
  BaseRadioGroup.Props<Value>,
  'style' | 'className'
> {
  className?: string;
}

/**
 * The set one `RadioOption` belongs to: it carries the value and hands each row
 * its checked state, so exactly one of them is ever picked.
 *
 * It has no visible label of its own — the heading above the cards is the app's,
 * so pass `aria-label` to say what is being chosen.
 */
export function RadioOptionGroup<Value = string>({
  className,
  ...props
}: RadioOptionGroupProps<Value>) {
  return (
    <BaseRadioGroup
      data-slot='radio-option-group'
      className={cn('flex flex-col gap-2', className)}
      {...props}
    />
  );
}

/**
 * One option in a short list where exactly one is true — which of two things
 * Wundo should stop doing for a meeting, say.
 *
 * The row itself is the control, which is what makes the whole bordered
 * rectangle clickable: a `<label>` around it would not forward a click, because
 * what sits underneath is Base UI's own element rather than a native input.
 *
 * It is a `CheckOption` with a round mark and a second line, and it is a
 * separate component for the same reason radios are not checkboxes: the choice
 * is exclusive, so the group — not the row — owns the value.
 */
export function RadioOption<Value = string>({
  label,
  description,
  className,
  ...props
}: RadioOptionProps<Value>) {
  return (
    <BaseRadio.Root
      data-slot='radio-option'
      className={cn('group', radioOptionVariants(), className)}
      {...props}
    >
      <span
        data-slot='radio-option-mark'
        aria-hidden='true'
        className={cn(
          /* `mt-0.5` lines the mark up with the label's cap height, not the card. */
          'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border-2 border-line transition',
          'group-data-checked:border-ink',
        )}
      >
        <BaseRadio.Indicator className='size-2.5 rounded-full bg-ink' />
      </span>
      <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
        <span className='text-base font-semibold text-ink'>{label}</span>
        {description && <span className='text-sm text-ink-muted'>{description}</span>}
      </span>
    </BaseRadio.Root>
  );
}
